// /frontend/src/App.tsx - OPTIMIZED
import React, { useState, useEffect, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from './components/ErrorBoundary';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import './lib/i18n';
import { PageTracker } from './lib/usePageTracking'; // 📊 Analytics
import ProtectedRoute from './components/ProtectedRoute';
import PlatformStaffRoute from './components/PlatformStaffRoute';
import MonitoringRoute from './components/MonitoringRoute';
import { lazyWithRetry } from './lib/lazyImport';
import { condoRoutes } from './modules/condo/routes';

declare global {
  interface Window {
    __lenis?: any;
  }
}
const Layout = lazyWithRetry(() => import('./components/Layout/Layout'), 'Layout');
const EmployeeActivation = lazyWithRetry(() => import('./pages/EmployeeActivation'), 'EmployeeActivation');
const EmployeeSchedulePage = lazyWithRetry(() => import('./pages/EmployeeSchedulePage'), 'EmployeeSchedulePage');
const UserActivation = lazyWithRetry(() => import('./pages/UserActivation'), 'UserActivation');
const SmartActivation = lazyWithRetry(() => import('./components/SmartActivation'), 'SmartActivation');
const BillingPage = lazyWithRetry(() => import('./pages/BillingPage'), 'BillingPage');
const ForgotPassword = lazyWithRetry(() => import('./pages/ForgotPassword'), 'ForgotPassword');
const ResetPassword = lazyWithRetry(() => import('./pages/ResetPassword'), 'ResetPassword');
const OpenAppRedirect = lazyWithRetry(() => import('./pages/OpenAppRedirect'), 'OpenAppRedirect');
const CondoInviteAccept = lazyWithRetry(() => import('./pages/CondoInviteAccept'), 'CondoInviteAccept');

// ===== PÁGINAS PÚBLICAS (NUEVAS) =====
import LandingPage from './pages/LandingPage';
const PlatformOverview = lazyWithRetry(() => import('./pages/PlatformOverview'), 'PlatformOverview');
const SegmentedLanding = lazyWithRetry(() => import('./pages/SegmentedLanding'), 'SegmentedLanding');
const SegmentLandingDeep = lazyWithRetry(() => import('./pages/SegmentLandingDeep'), 'SegmentLandingDeep');
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'), 'PrivacyPolicy');
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'), 'TermsOfService');
const Onboarding = lazyWithRetry(() => import('./pages/Onboarding'), 'Onboarding');
const OneTimePayment = lazyWithRetry(() => import('./pages/OneTimePayment'), 'OneTimePayment');
const AccountDelete = lazyWithRetry(() => import('./pages/AccountDelete'), 'AccountDelete');
const NotFound = lazyWithRetry(() => import('./pages/NotFound'), 'NotFound');

import { Toaster } from 'react-hot-toast';
import AIChatWidget from './components/AIChat/AIChatWidget';
// GoogleCalendarCallback removed - Google Calendar feature disabled
// Import all pages
import { LoadingLogo } from './components/Common/LoadingLogo';
const Login = lazyWithRetry(() => import('./pages/Login'), 'Login');
const Properties = lazyWithRetry(() => import('./pages/Properties'), 'Properties');
const Bookings = lazyWithRetry(() => import('./pages/Bookings'), 'Bookings');
const Leases = lazyWithRetry(() => import('./pages/Leases'), 'Leases');
const Calendar = lazyWithRetry(() => import('./pages/Calendar'), 'Calendar');
const Tasks = lazyWithRetry(() => import('./pages/Tasks'), 'Tasks');
const DeletedTasks = lazyWithRetry(() => import('./pages/DeletedTasks'), 'DeletedTasks');
const Employees = lazyWithRetry(() => import('./pages/Employees'), 'Employees');
const Integrations = lazyWithRetry(() => import('./pages/Integrations'), 'Integrations');
const Users = lazyWithRetry(() => import('./pages/admin/Users'), 'Users');
const TenantUsers = lazyWithRetry(() => import('./pages/admin/Users').then(module => ({ default: module.TenantUsers })), 'TenantUsers');
const TaskTemplates = lazyWithRetry(() => import('./pages/TaskTemplates'), 'TaskTemplates');
const Messages = lazyWithRetry(() => import('./pages/Messages'), 'Messages');
const Monitoring = lazyWithRetry(() => import('./pages/Monitoring'), 'Monitoring');
const AdminLogs = lazyWithRetry(() => import('./pages/AdminLogs'), 'AdminLogs');
// const TenantRegistration = React.lazy(() => import('./pages/TenantRegistration'));
const PropertyImport = lazyWithRetry(() => import('./pages/PropertyImport'), 'PropertyImport');
const EnhancedDashboard = lazyWithRetry(() => import('./pages/EnhancedDashboard'), 'EnhancedDashboard');
const Settings = lazyWithRetry(() => import('./pages/Settings'), 'Settings');
const Profile = lazyWithRetry(() => import('./pages/Profile'), 'Profile');
const Notifications = lazyWithRetry(() => import('./pages/Notifications'), 'Notifications');
const CalendarExportPage = lazyWithRetry(() => import('./pages/CalendarExportPage'), 'CalendarExportPage');
const PerformanceDashboard = lazyWithRetry(() => import('./pages/Performancedashboard'), 'PerformanceDashboard');
const Reports = lazyWithRetry(() => import('./pages/Reports'), 'Reports');
const Inventory = lazyWithRetry(() => import('./pages/Inventory'), 'Inventory');
const WelcomeBooks = lazyWithRetry(() => import('./pages/WelcomeBooks'), 'WelcomeBooks');
const OperationsMap = lazyWithRetry(() => import('./pages/OperationsMap'), 'OperationsMap');
const Invoices = lazyWithRetry(() => import('./pages/Invoices'), 'Invoices');
const Expenses = lazyWithRetry(() => import('./pages/Expenses'), 'Expenses');
const GuestGuideView = lazyWithRetry(() => import('./pages/guest/GuestGuideView'), 'GuestGuideView');
const GuestWelcomeBook = lazyWithRetry(() => import('./pages/guest/GuestWelcomeBook'), 'GuestWelcomeBook');
const PublicTaskEvidencePage = lazyWithRetry(() => import('./pages/guest/PublicTaskEvidencePage'), 'PublicTaskEvidencePage');
const OwnerDashboard = lazyWithRetry(() => import('./pages/owner/OwnerDashboard'), 'OwnerDashboard');
const OwnerThreads = lazyWithRetry(() => import('./pages/owner/OwnerThreads'), 'OwnerThreads');
const Owners = lazyWithRetry(() => import('./pages/Owners'), 'Owners');
const Tickets = lazyWithRetry(() => import('./pages/Tickets'), 'Tickets');
const PropertyDetails = lazyWithRetry(() => import('./pages/owner/PropertyDetails'), 'PropertyDetails');
const RenterDashboard = lazyWithRetry(() => import('./pages/renter/RenterDashboard'), 'RenterDashboard');
const RenterThreads = lazyWithRetry(() => import('./pages/renter/RenterThreads'), 'RenterThreads');
const ReviewTemplates = lazyWithRetry(() => import('./pages/ReviewTemplates'), 'ReviewTemplates');
const PerformanceReviews = lazyWithRetry(() => import('./pages/PerformanceReviews'), 'PerformanceReviews');
const TrainingCenter = lazyWithRetry(() => import('./pages/TrainingCenter'), 'TrainingCenter');
const Company = lazyWithRetry(() => import('./pages/Company'), 'Company');
const PortfolioServices = lazyWithRetry(() => import('./pages/PortfolioServices'), 'PortfolioServices');
const Announcements = lazyWithRetry(() => import('./pages/Announcements'), 'Announcements');
const GifManager = lazyWithRetry(() => import('./pages/GifManager'), 'GifManager');
const OnboardingInvitations = lazyWithRetry(() => import('./pages/admin/OnboardingInvitations'), 'OnboardingInvitations');
const LiveOpsWall = lazyWithRetry(() => import('./pages/LiveOpsWall'), 'LiveOpsWall');


import LockedLayout from './components/Layout/LockedLayout';

// ===== LAZY LOADED AUTHENTICATED PROVIDERS AND COMPONENT WRAPPER =====
const NotificationProvider = lazyWithRetry(() => import('./contexts/NotificationContext').then(m => ({ default: m.NotificationProvider })), 'NotificationProvider');
const TimeTrackingProvider = lazyWithRetry(() => import('./contexts/TimeTrackingContext').then(m => ({ default: m.TimeTrackingProvider })), 'TimeTrackingProvider');
const UploadProvider = lazyWithRetry(() => import('./contexts/UploadContext').then(m => ({ default: m.UploadProvider })), 'UploadProvider');
const UploadProgressWidget = lazyWithRetry(() => import('./components/Common/UploadProgressWidget'), 'UploadProgressWidget');
const SessionManager = lazyWithRetry(() => import('./components/SessionManager').then(m => ({ default: m.SessionManager })), 'SessionManager');

// Wrapper for /live-dashboard: minimal providers to avoid the 10+ parallel
// preload requests that NotificationProvider + TimeTrackingProvider + Header
// + NotificationsCenter fire on mount. Those sum ~8 seconds blocking main
// thread and cause the user-visible "20s load time" even though the live
// wall endpoint itself returns in 600ms.
function LiveOpsWallLightWrapper() {
  return (
    <Suspense fallback={<div className="h-screen w-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950"><LoadingLogo size="xl" /></div>}>
      <ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}>
        <LiveOpsWall />
      </ProtectedRoute>
    </Suspense>
  );
}

function ExpensesRouteGuard() {
  const { user } = useAuth();
  const businessType = String(user?.tenant_business_type || '').trim().toLowerCase();
  const hasExpensesAccess = businessType === 'long_term_rental' || businessType === 'hybrid';

  if (!hasExpensesAccess) {
    return <Navigate to="/tasks" replace />;
  }

  return <Expenses />;
}

function ProtectedLayoutWrapper() {
  return (
    <Suspense fallback={<div className="h-screen flex items-center justify-center"><LoadingLogo size="xl" /></div>}>
      <NotificationProvider>
        <TimeTrackingProvider>
          <UploadProvider>
            <LayoutSelector />
            <UploadProgressWidget />
            <SessionManager />
          </UploadProvider>
        </TimeTrackingProvider>
      </NotificationProvider>
    </Suspense>
  );
}

// ===== COMPONENTE PARA PÁGINAS PÚBLICAS (SIN AUTHCONTEXT) =====
function PublicPageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="public-page marketing">
      {children}
    </div>
  );
}

// ✅ LAYOUT SELECTOR COMPONENT
// ✅ LAYOUT SELECTOR COMPONENT
function LayoutSelector() {
  const { user, isLoading } = useAuth();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isInactiveReason = searchParams.get('reason') === 'inactive';

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <LoadingLogo size="xl" />
      </div>
    );
  }

  // Check if tenant is inactive/expired OR URL indicates inactivity
  if (user?.tenant_status === 'inactive' || isInactiveReason) {
    return <LockedLayout />;
  }

  return <Layout />;
}

// ✅ Scroll logic for hash links + route scroll-top (works with Layout main overflow-y-auto scroll container)
function ScrollToHashElement() {
  const location = useLocation();

  const getMainScroll = (): HTMLElement | null => {
    if (typeof document === 'undefined') return null;
    const layoutMain = document.querySelector('main[data-scroll-root]') as HTMLElement | null;
    if (layoutMain) return layoutMain;
    const mains = document.querySelectorAll('main');
    for (let i = 0; i < mains.length; i++) {
      const m = mains[i] as HTMLElement;
      const styles = window.getComputedStyle(m);
      if (styles.overflowY === 'auto' || styles.overflowY === 'scroll') return m;
    }
    return null;
  };

  const scrollToId = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return;
    const mainScroll = getMainScroll();
    if (mainScroll) {
      const rect = element.getBoundingClientRect();
      const mainRect = mainScroll.getBoundingClientRect();
      const offsetTop = rect.top - mainRect.top + mainScroll.scrollTop - 20;
      mainScroll.scrollTo({ top: offsetTop, behavior: 'smooth' });
    } else {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  useEffect(() => {
    if (location.hash) {
      const id = decodeURIComponent(location.hash.replace('#', ''));
      // Las páginas públicas se cargan con lazy(): el ancla puede no existir aún al
      // cambiar de ruta. Reintentamos hasta que aparezca (máx. ~4 s) y luego
      // reajustamos un par de veces por si el layout se movió (imágenes, fuentes).
      const timers: ReturnType<typeof setTimeout>[] = [];
      let waited = 0;
      const attempt = () => {
        if (document.getElementById(id)) {
          scrollToId(id);
          timers.push(setTimeout(() => scrollToId(id), 450));
          timers.push(setTimeout(() => scrollToId(id), 1000));
          return;
        }
        if (waited >= 4000) return;
        waited += 100;
        timers.push(setTimeout(attempt, 100));
      };
      attempt();
      return () => { timers.forEach(clearTimeout); };
    } else {
      const mainScroll = getMainScroll();
      if (mainScroll) {
        mainScroll.scrollTo({ top: 0, behavior: 'auto' });
      } else {
        window.scrollTo(0, 0);
      }
    }
  }, [location.pathname, location.hash]);

  return null;
}

// ✅ APP CONTENT (Children of Providers)
function AppContent() {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    // ✅ SAFETY RESET: Fix completo de overflow residual
    const resetAllOverflows = () => {
      const nodes = [
      document.documentElement,
      document.body,
      document.getElementById('root') as HTMLElement | null,
    ].filter(Boolean) as HTMLElement[];
      nodes.forEach((n) => {
        if (!n) return;
        n.style.overflow = '';
        n.style.overflowY = '';
        n.style.overflowX = '';
      });
      delete (window as any).__synclyst_modal_lock_original_overflow__;
    };

    resetAllOverflows();
    const tm1 = window.setTimeout(resetAllOverflows, 50);
    const tm2 = window.setTimeout(resetAllOverflows, 500);
    return () => { window.clearTimeout(tm1); window.clearTimeout(tm2); }
  }, []);

  return (
    <Router>
      <AppRoutesAndChrome />
    </Router>
  );
}

function AppRoutesAndChrome() {
  const { isAuthenticated } = useAuth(); // Safe to use here (inside Router + AuthProvider)
  const location = useLocation();

  // AI widget ONLY renders inside the authenticated product (routes wrapped by ProtectedLayoutWrapper).
  // Public pages (landing, login, onboarding, reset-pass, evidence shares, welcome books, ...),
  // auth flows and 404 never show the assistant — even when a session exists in the browser.
  const PUBLIC_PATH_PATTERNS: Array<string | RegExp> = [
    '/',
    '/home',
    '/help',
    '/platform',
    /^\/segment(\/.*)?$/,
    /^\/evidence(\/.*)?$/,
    /^\/welcome(\/.*)?$/,
    /^\/guest\/guide(\/.*)?$/,
    '/login',
    /^\/register\/?$/,
    '/forgot-password',
    '/reset-password',
    '/open-app',
    '/privacy',
    '/terms',
    '/company',
    '/services',
    /^\/account\/delete\/?$/,
    /^\/onboarding(\/.*)?$/,
    /^\/activate(\/.*)?$/,
    /^\/activate-employee\/?$/,
    /^\/activate-account\/?$/,
    /^\/auth\/callback\/.*$/,
  ];

  const isPublicRoute = PUBLIC_PATH_PATTERNS.some((pattern) =>
    typeof pattern === 'string' ? location.pathname === pattern : pattern.test(location.pathname)
  );
  const isLiveDashboardRoute = location.pathname === '/live-dashboard';
  const [showAIAssistant, setShowAIAssistant] = useState<boolean>(() => {
    const stored = localStorage.getItem('show_ai_assistant');
    return stored === null ? true : stored === 'true';
  });

  useEffect(() => {
    const handleVisibilityChange = () => {
      const stored = localStorage.getItem('show_ai_assistant');
      setShowAIAssistant(stored === null ? true : stored === 'true');
    };
    window.addEventListener('ai_assistant_visibility_changed', handleVisibilityChange);
    window.addEventListener('storage', handleVisibilityChange);
    return () => {
      window.removeEventListener('ai_assistant_visibility_changed', handleVisibilityChange);
      window.removeEventListener('storage', handleVisibilityChange);
    };
  }, []);

  const showAIChat = isAuthenticated && !isPublicRoute && !isLiveDashboardRoute && showAIAssistant;

  return (
    <>
      <ScrollToHashElement />
      <div className="App">
        <PageTracker /> {/* 📊 Auto-track all page views */}
        <Suspense fallback={<div className="h-screen flex items-center justify-center"><LoadingLogo size="xl" /></div>}>
          <Routes>
            {/* ===== RUTAS PÚBLICAS ESPECÍFICAS (SIN LAYOUT) ===== */}
            {/* SEO IMPORTANTE:
                En PRODUCCIÓN IONOS, Nginx captura estas rutas ANTES y devuelve 301 real
                (ver scripts/nginx-synclyst.com.conf). Estos <Navigate> solo son fallback
                para desarrollo local (npm run dev) sin Nginx. */}
            <Route path="/" element={<RootRedirect />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/home/" element={<Navigate to="/" replace />} />
            <Route path="/help" element={<Navigate to="/" replace />} />
            <Route path="/help/" element={<Navigate to="/" replace />} />

            <Route path="/privacy" element={
              <PublicPageWrapper>
                <PrivacyPolicy />
              </PublicPageWrapper>
            } />

            <Route path="/terms" element={
              <PublicPageWrapper>
                <TermsOfService />
              </PublicPageWrapper>
            } />

            <Route path="/account/delete" element={
              <PublicPageWrapper>
                <AccountDelete />
              </PublicPageWrapper>
            } />

            <Route path="/company" element={
              <PublicPageWrapper>
                <Company />
              </PublicPageWrapper>
            } />

            <Route path="/services" element={
              <PublicPageWrapper>
                <PortfolioServices />
              </PublicPageWrapper>
            } />

            <Route path="/platform" element={
              <PublicPageWrapper>
                <PlatformOverview />
              </PublicPageWrapper>
            } />

            <Route path="/segment/:segmentId" element={
              <PublicPageWrapper>
                <SegmentLandingDeep />
              </PublicPageWrapper>
            } />

            <Route path="/onboarding" element={
              <PublicPageWrapper>
                <Onboarding />
              </PublicPageWrapper>
            } />

            <Route path="/onboarding/us" element={
              <PublicPageWrapper>
                <Onboarding country="us" />
              </PublicPageWrapper>
            } />

            <Route path="/onboarding/mx" element={
              <PublicPageWrapper>
                <Onboarding country="mx" />
              </PublicPageWrapper>
            } />

            <Route path="/pay" element={<OneTimePayment />} />
            <Route path="/pay/us" element={<OneTimePayment country="us" />} />
            <Route path="/pay/mx" element={<OneTimePayment country="mx" />} />
            <Route path="/pay/success" element={<OneTimePayment />} />
            <Route path="/pay/cancel" element={<OneTimePayment />} />

            {/* ===== RUTAS DE AUTENTICACIÓN (SIN LAYOUT) ===== */}
            {/* /register redirect: Nginx devuelve 301 en producción. Fallback JS para dev local. */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Navigate to="/onboarding" replace />} />
            <Route path="/register/" element={<Navigate to="/onboarding" replace />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/open-app" element={<OpenAppRedirect />} />
            {/* Invitacion publica a condominio: el email manda /condo-invite#token=... */}
            <Route path="/condo-invite" element={<CondoInviteAccept />} />

            {/* Rutas de activación - Fuera del Layout principal */}
            <Route path="/activate-employee" element={<EmployeeActivation />} />
            <Route path="/activate-account" element={<UserActivation />} />
            <Route path="/activate/:token" element={<SmartActivation />} />

            {/* Public Guest Guide Route */}
            <Route path="/welcome/:slug" element={<GuestWelcomeBook />} />
            {/* Public Task Evidence Share Route */}
            <Route path="/evidence/:slug" element={<PublicTaskEvidencePage />} />
            <Route path="/guest/guide/:token" element={<GuestGuideView />} />

            {/* GoogleCalendarCallback route removed - feature disabled */}

            {/* ===== RUTAS PROTEGIDAS CON LAYOUT DINÁMICO ===== */}
            {/* ❌ REMOVED KEY PROP that caused remounts on tenant_status change */}
            <Route element={<ProtectedLayoutWrapper />}>
              {/* ===== MANAGER/OWNER/ADMIN ONLY ROUTES ===== */}
              <Route path="/dashboard" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><EnhancedDashboard /></ProtectedRoute>} />
              <Route path="/calendar" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><Calendar /></ProtectedRoute>} />
              <Route path="/properties" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><Properties /></ProtectedRoute>} />
              {/* ===== CONDOMINIOS / HOA (spec 008, fase A) ===== */}
              {condoRoutes()}
              <Route path="/bookings" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><Bookings /></ProtectedRoute>} />
              <Route path="/leases" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager', 'property_owner', 'renter']}><Leases /></ProtectedRoute>} />
              {/* ✅ NEW: Employee Schedule Route */}
              <Route path="/schedule" element={<ProtectedRoute allowedRoles={['employee', 'admin', 'manager']}><EmployeeSchedulePage /></ProtectedRoute>} />
              <Route path="/tasks" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager', 'employee']}><Tasks /></ProtectedRoute>} />
              <Route path="/tasks/deleted" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><DeletedTasks /></ProtectedRoute>} />
              <Route path="/announcements" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager', 'employee', 'property_owner', 'renter']}><Announcements /></ProtectedRoute>} />
              <Route path="/tickets" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager', 'employee', 'property_owner', 'renter']}><Tickets /></ProtectedRoute>} />
              <Route path="/employees" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><Employees /></ProtectedRoute>} />
              <Route path="/owners" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><Owners /></ProtectedRoute>} />
              <Route path="/integrations" element={<ProtectedRoute allowedRoles={['admin', 'owner']}><Integrations /></ProtectedRoute>} />
              <Route path="/users" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><TenantUsers /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['admin']}><Users /></ProtectedRoute>} />
              <Route path="/task-templates" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><TaskTemplates /></ProtectedRoute>} />
              <Route path="/performance-reviews" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><PerformanceReviews /></ProtectedRoute>} />
              <Route path="/review-templates" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><ReviewTemplates /></ProtectedRoute>} />
              <Route path="/training" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager', 'employee']}><TrainingCenter /></ProtectedRoute>} />
              <Route path="/my-trainings" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager', 'employee']}><TrainingCenter /></ProtectedRoute>} />
              <Route path="/messages" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager', 'employee', 'property_owner', 'renter']}><Messages /></ProtectedRoute>} />
              <Route path="/monitoring" element={<MonitoringRoute><Monitoring /></MonitoringRoute>} />
              <Route path="/admin/logs" element={<PlatformStaffRoute><AdminLogs /></PlatformStaffRoute>} />
              <Route path="/import-properties" element={<ProtectedRoute allowedRoles={['admin', 'owner']}><PropertyImport /></ProtectedRoute>} />
              <Route path="/admin/gifs" element={<ProtectedRoute allowedRoles={['admin']}><GifManager /></ProtectedRoute>} />
              <Route path="/admin/invitations" element={<ProtectedRoute allowedRoles={['admin']}><OnboardingInvitations /></ProtectedRoute>} />

              {/* ===== ALL AUTHENTICATED USERS ===== */}
              <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
              <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
              <Route path="/calendar-export" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager', 'employee']}><CalendarExportPage /></ProtectedRoute>} />
              <Route path="/analytics" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><PerformanceDashboard /></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><Reports /></ProtectedRoute>} />
              <Route path="/inventory" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><Inventory /></ProtectedRoute>} />
              <Route path="/map" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><OperationsMap /></ProtectedRoute>} />
              <Route path="/welcome-books" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><WelcomeBooks /></ProtectedRoute>} />
              <Route path="/invoices" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager']}><Invoices /></ProtectedRoute>} />
              <Route path="/expenses" element={<ProtectedRoute allowedRoles={['admin', 'owner', 'manager', 'employee']}><ExpensesRouteGuard /></ProtectedRoute>} />
              <Route path="/owner/dashboard" element={<ProtectedRoute allowedRoles={['property_owner', 'admin', 'owner', 'manager']}><OwnerDashboard /></ProtectedRoute>} />
              <Route path="/owner/threads" element={<ProtectedRoute allowedRoles={['property_owner', 'admin', 'owner', 'manager']}><OwnerThreads /></ProtectedRoute>} />
              <Route path="/owner/properties/:id" element={<ProtectedRoute allowedRoles={['property_owner', 'admin', 'owner', 'manager']}><PropertyDetails /></ProtectedRoute>} />

              {/* ===== RENTER (INQUILINO) ROUTES ===== */}
              <Route path="/renter/dashboard" element={<ProtectedRoute allowedRoles={['renter', 'admin', 'owner', 'manager']}><RenterDashboard /></ProtectedRoute>} />
              <Route path="/renter/threads" element={<ProtectedRoute allowedRoles={['renter', 'admin', 'owner', 'manager', 'property_owner']}><RenterThreads /></ProtectedRoute>} />

              {/* ✅ BILLING: Owner/Admin only */}
              <Route path="billing" element={
                <ProtectedRoute allowedRoles={['admin', 'owner']}>
                  <BillingPage />
                </ProtectedRoute>
              } />

              <Route path="profile" element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              } />

              <Route path="notifications" element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              } />
              {/* GoogleCalendarCallback removed - Google Calendar feature disabled */}
            </Route>

            {/* ===== Live Ops Wall (LIGHTWEIGHT — OUTSIDE ProtectedLayoutWrapper) =====
                 Skip NotificationProvider + TimeTrackingProvider + UploadProvider
                 + Layout/Header/NotificationsCenter. Those fire 10+ parallel
                 requests on mount (notifs/summary x2, time-tracking/active-session x2,
                 auth/me x2, plus React dev compile) which add ~15 seconds of
                 blocking MAIN THREAD work — even though the live-wall endpoint
                 itself returns in 600ms. */}
            <Route path="/live-dashboard" element={<LiveOpsWallLightWrapper />} />

            {/* ===== 404 CATCH-ALL ===== */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>

        {/* Development Auth Status */}
        <Toaster
          position="top-center"
          toastOptions={{
            duration: 4000,
            // Tokens de diseño (bg.inverse / text.inverse); sin colores fijos. El estado lo da el icono + texto.
            style: {
              background: 'var(--bg-inverse)',
              color: 'var(--text-inverse)',
            },
            success: {
              duration: 3000,
              iconTheme: { primary: 'var(--success-fg)', secondary: 'var(--bg-inverse)' },
            },
            error: {
              duration: 5000,
              iconTheme: { primary: 'var(--danger-fg)', secondary: 'var(--bg-inverse)' },
            },
          }}
        />
        {showAIChat && <AIChatWidget />}
      </div>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <AuthProvider>
          <ThemeProvider>
            <AppContent />
          </ThemeProvider>
        </AuthProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

// ===== COMPONENTE PARA MANEJAR LA RUTA RAÍZ ===== 
function RootRedirect() {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="flex justify-center">
            <LoadingLogo size="xl" />
          </div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Si no está autenticado, renderizar la landing segmentada en la ruta raíz
  if (!isAuthenticated) {
    return (
      <PublicPageWrapper>
        <SegmentedLanding />
      </PublicPageWrapper>
    );
  }

  // ✅ Empleado → My Schedule
  if (user?.role === 'employee') {
    return <Navigate to="/announcements" replace />;
  }

  // ✅ Property Owner → Owner Dashboard
  if (user?.role === 'property_owner') {
    return <Navigate to="/announcements" replace />;
  }

  // Otros roles → Dashboard
  return <Navigate to="/announcements" replace />;
}

export default App;
